import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../Context/LanguageContext';
import SEO from '../Components/SEO';

export default function OrderConfirmation() {
  const location = useLocation();
  const { language } = useLanguage();
  const { customerData, orderData, totalAmount, discountAmount, subtotal, shippingCost } = location.state || {};

  if (!customerData || !orderData) {
    return (
      <>
        <SEO
          title="Order Confirmation"
          description="Order confirmation - VetCare Store"
          noIndex={true}
          canonical={typeof window !== 'undefined' ? `${window.location.origin}/order-confirmation` : ''}
          type="website"
          locale={language === 'ar' ? 'ar_EG' : 'en_EG'}
          htmlLang={language === 'ar' ? 'ar' : 'en'}
          htmlDir={language === 'ar' ? 'rtl' : 'ltr'}
        />
        <div style={{ textAlign: 'center', padding: '120px 20px', minHeight: '60vh' }}>
          <h2>Order Not Found</h2>
          <p>No order data available.</p>
        </div>
      </>
    );
  }

  const formatPrice = (price) => {
    return Number(price).toFixed(2);
  };

  return (
    <>
      <SEO
        title={language === 'ar' ? 'تأكيد الطلب' : 'Order Confirmation'}
        description={language === 'ar' ? 'تأكيد الطلب - VetCare Store' : 'Order confirmation - VetCare Store'}
        noIndex={true}
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/order-confirmation` : ''}
        type="website"
        locale={language === 'ar' ? 'ar_EG' : 'en_EG'}
        htmlLang={language === 'ar' ? 'ar' : 'en'}
        htmlDir={language === 'ar' ? 'rtl' : 'ltr'}
      />
      <div className="order-confirmation-page" style={{ 
        maxWidth: '900px', 
        margin: '40px auto', 
        padding: '20px',
        fontFamily: 'var(--ff-nunito_sans)',
        backgroundColor: '#fff',
        boxShadow: '0 0 20px rgba(0,0,0,0.1)',
        borderRadius: '8px'
      }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px', borderBottom: '2px solid var(--portland-orange)', paddingBottom: '20px' }}>
        <h1 style={{ 
          color: 'var(--portland-orange)', 
          fontSize: '2.5rem',
          marginBottom: '10px',
          fontFamily: 'var(--ff-bangers)'
        }}>
          {language === 'ar' ? 'تأكيد الطلب' : 'Order Confirmation'}
        </h1>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>
          {language === 'ar' ? ' VetCare Store - متجر العناية بالحيوانات الأليفة' : 'VetCare Store - Pet Care Supplies'}
        </p>
        <div style={{ 
          marginTop: '15px', 
          padding: '10px 20px', 
          backgroundColor: '#e8f5e8', 
          borderRadius: '20px', 
          display: 'inline-block',
          color: '#2d5a2d',
          fontWeight: 'bold'
        }}>
          {language === 'ar' ? 'تم استلام طلبك بنجاح!' : 'Order Received Successfully!'}
        </div>
      </div>

      {/* Customer Information */}
      <div style={{ marginBottom: '30px', padding: '25px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
        <h2 style={{ 
          color: 'var(--portland-orange)', 
          marginBottom: '20px',
          fontSize: '1.5rem',
          borderBottom: '2px solid #e0e0e0',
          paddingBottom: '10px'
        }}>
          {language === 'ar' ? 'معلومات العميل' : 'Customer Information'}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
          <div>
            <strong>{language === 'ar' ? 'الاسم الكامل:' : 'Full Name:'}</strong>
            <p style={{ margin: '5px 0', color: '#555' }}>
              {customerData.firstName} {customerData.lastName}
            </p>
          </div>
          <div>
            <strong>{language === 'ar' ? 'رقم الهاتف:' : 'Phone Number:'}</strong>
            <p style={{ margin: '5px 0', color: '#555' }}>{customerData.phone}</p>
          </div>
          <div>
            <strong>{language === 'ar' ? 'البريد الإلكتروني:' : 'Email:'}</strong>
            <p style={{ margin: '5px 0', color: '#555' }}>{customerData.email}</p>
          </div>
          <div>
            <strong>{language === 'ar' ? 'المحافظة:' : 'Governorate:'}</strong>
            <p style={{ margin: '5px 0', color: '#555' }}>{customerData.governorate}</p>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <strong>{language === 'ar' ? 'عنوان التوصيل:' : 'Delivery Address:'}</strong>
            <p style={{ margin: '5px 0', color: '#555' }}>
              {customerData.address}
              {customerData.apartment && `, ${customerData.apartment}`}
            </p>
          </div>
          <div>
            <strong>{language === 'ar' ? 'المدينة:' : 'City:'}</strong>
            <p style={{ margin: '5px 0', color: '#555' }}>{customerData.city}</p>
          </div>
          <div>
            <strong>{language === 'ar' ? 'الرمز البريدي:' : 'Postal Code:'}</strong>
            <p style={{ margin: '5px 0', color: '#555' }}>{customerData.postalCode || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          color: 'var(--portland-orange)', 
          marginBottom: '20px',
          fontSize: '1.5rem',
          borderBottom: '2px solid #e0e0e0',
          paddingBottom: '10px'
        }}>
          {language === 'ar' ? 'تفاصيل الطلب' : 'Order Details'}
        </h2>
        <div style={{ backgroundColor: '#f9f9f9', borderRadius: '8px', overflow: 'hidden' }}>
          {/* Table Header */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', 
            padding: '15px',
            backgroundColor: '#e8e8e8',
            fontWeight: 'bold',
            borderBottom: '2px solid #ddd'
          }}>
            <div>{language === 'ar' ? 'المنتج' : 'Product'}</div>
            <div style={{ textAlign: 'center' }}>{language === 'ar' ? 'سعر القطعة' : 'Unit Price'}</div>
            <div style={{ textAlign: 'center' }}>{language === 'ar' ? 'الكمية' : 'Quantity'}</div>
            <div style={{ textAlign: 'center' }}>{language === 'ar' ? 'سعر الكمية' : 'Subtotal'}</div>
            <div style={{ textAlign: 'center' }}>{language === 'ar' ? 'الإجمالي' : 'Total'}</div>
          </div>

          {/* Table Rows */}
          {orderData.map((item, index) => {
            const unitPrice = item.selectedPrice || item.price || 0;
            const quantity = item.quantity || 1;
            const subtotalPrice = unitPrice * quantity;
            const totalPrice = subtotalPrice; // Can be modified if there are item-specific discounts

            return (
              <div key={item.id} style={{ 
                display: 'grid', 
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', 
                padding: '15px',
                borderBottom: '1px solid #eee',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                    {language === 'ar' ? (item.nameAr || item.name) : (item.nameEn || item.name)}
                  </div>
                  {item.selectedTaste && (
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      {typeof item.selectedTaste === 'object' ? item.selectedTaste.name : item.selectedTaste}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <strong>{formatPrice(unitPrice)} EGP</strong>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ 
                    backgroundColor: '#f0f0f0', 
                    padding: '5px 10px', 
                    borderRadius: '15px',
                    fontWeight: 'bold'
                  }}>
                    {quantity}
                  </span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <strong>{formatPrice(subtotalPrice)} EGP</strong>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <strong style={{ color: 'var(--portland-orange)' }}>
                    {formatPrice(totalPrice)} EGP
                  </strong>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Order Summary */}
      <div style={{ 
        padding: '25px', 
        backgroundColor: '#f9f9f9', 
        borderRadius: '8px',
        marginBottom: '30px'
      }}>
        <h2 style={{ 
          color: 'var(--portland-orange)', 
          marginBottom: '20px',
          fontSize: '1.5rem',
          borderBottom: '2px solid #e0e0e0',
          paddingBottom: '10px'
        }}>
          {language === 'ar' ? 'ملخص الطلب' : 'Order Summary'}
        </h2>
        
        {/* Subtotal */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '1.1rem' }}>
            {language === 'ar' ? 'الإجمالي الفرعي:' : 'Subtotal:'}
            <span style={{ fontSize: '0.9rem', color: '#666', marginLeft: '10px' }}>
              ({language === 'ar' ? 'سعر المنتجات' : 'Products Price'})
            </span>
          </span>
          <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
            {formatPrice(subtotal)} EGP
          </span>
        </div>

        {/* Shipping Cost */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '1.1rem' }}>
            {language === 'ar' ? 'تكلفة الشحن:' : 'Shipping Cost:'}
            <span style={{ fontSize: '0.9rem', color: '#666', marginLeft: '10px' }}>
              ({language === 'ar' ? 'توصيل للمنزل' : 'Home Delivery'})
            </span>
          </span>
          <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
            {shippingCost > 0 ? formatPrice(shippingCost) : '0.00'} EGP
          </span>
        </div>

        {/* Discount */}
        {discountAmount > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '10px',
            padding: '10px',
            backgroundColor: '#e8f5e8',
            borderRadius: '5px'
          }}>
            <span style={{ fontSize: '1.1rem', color: '#27ae60', fontWeight: 'bold' }}>
              {language === 'ar' ? 'الخصم:' : 'Discount:'}
              <span style={{ fontSize: '0.9rem', color: '#27ae60', marginLeft: '10px', fontWeight: 'normal' }}>
                ({language === 'ar' ? 'تم تطبيق خصم على الطلب' : 'Discount Applied to Order'})
              </span>
            </span>
            <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#27ae60' }}>
              -{formatPrice(discountAmount)} EGP
            </span>
          </div>
        )}

        {/* Calculation Breakdown */}
        {(discountAmount > 0 || shippingCost > 0) && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '5px', 
            marginBottom: '15px',
            fontSize: '0.95rem',
            color: '#555'
          }}>
            <div style={{ marginBottom: '5px' }}>
              <strong>{language === 'ar' ? 'طريقة الحساب:' : 'Calculation:'}</strong>
            </div>
            <div>
              {formatPrice(subtotal)} {language === 'ar' ? '(الإجمالي الفرعي)' : '(Subtotal)'} 
              {shippingCost > 0 && ` + ${formatPrice(shippingCost)} ${language === 'ar' ? '(الشحن)' : '(Shipping)'}`}
              {discountAmount > 0 && ` - ${formatPrice(discountAmount)} ${language === 'ar' ? '(الخصم)' : '(Discount)'}`}
              {" = "} <strong>{formatPrice(totalAmount)} EGP</strong>
            </div>
          </div>
        )}

        {/* Final Total */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '20px 0',
          borderTop: '3px solid var(--portland-orange)',
          marginTop: '15px'
        }}>
          <div>
            <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--portland-orange)' }}>
              {language === 'ar' ? 'المبلغ الإجمالي للدفع:' : 'Total Amount to Pay:'}
            </span>
            <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
              {language === 'ar' ? 'شامل الضريبة والشحن' : 'Including Tax and Shipping'}
            </div>
          </div>
          <div>
            <span style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--portland-orange)' }}>
              {formatPrice(totalAmount)} EGP
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ 
        textAlign: 'center', 
        padding: '25px', 
        borderTop: '2px solid #eee',
        color: '#666',
        fontSize: '0.9rem'
      }}>
        <p style={{ margin: '0', fontSize: '1.1rem', fontWeight: 'bold', color: '#333' }}>
          {language === 'ar' ? 'شكراً لاختيارك VetCare! سنقوم بمعالجة طلبك في أقرب وقت ممكن.' : 'Thank you for choosing VetCare! We will process your order as soon as possible.'}
        </p>
        <p style={{ margin: '15px 0 0 0' }}>
          {language === 'ar' ? 'رقم الطلب سيتم إرساله عبر البريد الإلكتروني ورسالة نصية' : 'Order number will be sent via email and SMS'}
        </p>
        <p style={{ margin: '10px 0 0 0' }}>
          {language === 'ar' ? 'للاستفسار: support@vetcare.com | 015 5355 5330' : 'For inquiries: support@vetcare.com | 015 5355 5330'}
        </p>
        <div style={{ marginTop: '20px' }}>
          <button 
            onClick={() => window.print()}
            style={{
              backgroundColor: 'var(--portland-orange)',
              color: 'white',
              padding: '12px 30px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginRight: '10px'
            }}
          >
            {language === 'ar' ? 'طباعة الفاتورة' : 'Print Invoice'}
          </button>
          <button 
            onClick={() => window.location.href = '/'}
            style={{
              backgroundColor: '#666',
              color: 'white',
              padding: '12px 30px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
          </button>
        </div>
      </div>
      </div>
    </>
  );
}
